import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEventSource, type SSEEvent } from "./useEventSource";
import { epicKeys } from "./queries/use-epics";
import { epicRequestKeys } from "./queries/use-epic-requests";
import { featureKeys } from "./queries/use-features";
import { taskKeys } from "./queries/use-tasks";
import { issueKeys } from "./queries/use-issues";
import { executionPlanKeys } from "./queries/use-execution-plan";
import { useToast } from "./useToast";
import { LIST_FALLBACK_POLL_MS } from "../lib/polling";
import { applyRunProgressEvent, RUN_PROGRESS_SSE_EVENT } from "./runProgressCache";

/**
 * Shape of entity mutation SSE event data.
 *
 * NOTE: The backend currently emits only `entityType` and `entityId`
 * (see packages/api/src/events/types.ts). The `epicId` and `featureId`
 * fields are included here for forward-compatibility — if the backend
 * is enriched to include parent context, scoped invalidation will
 * activate automatically. Until then, the broad-prefix fallbacks
 * (`issueKeys.all`, `executionPlanKeys.all()`) are the active code path.
 */
interface EntityEventData {
  entityType?: string;
  entityId?: string;
  epicId?: string;
  featureId?: string;
  [key: string]: unknown;
}

/**
 * Maps SSE events to React Query cache invalidation query keys.
 *
 * @param hookEpicId - The epicId from the useLiveUpdates hook context,
 *   used as a fallback when the SSE payload lacks parent IDs.
 */
export function getQueryKeysToInvalidate(
  event: SSEEvent,
  hookEpicId?: string,
): Array<readonly unknown[]> {
  const queryKeys: Array<readonly unknown[]> = [];
  const data = event.data as EntityEventData;

  if (!data || typeof data !== "object") {
    return queryKeys;
  }

  const { entityType, entityId, featureId } = data;
  // Prefer epicId from SSE payload; fall back to hook-level epicId
  const epicId = data.epicId || hookEpicId;

  switch (event.type) {
    case "entity.created":
      if (entityType === "epicRequest") {
        queryKeys.push(epicRequestKeys.lists());
      } else if (entityType === "epic") {
        // Invalidate all epic lists
        queryKeys.push(epicKeys.lists());
      } else if (entityType === "feature") {
        // Invalidate all feature lists
        queryKeys.push(featureKeys.lists());
        // Invalidate issue/execution-plan caches (scoped if epicId available)
        if (epicId) {
          queryKeys.push(epicKeys.detail(epicId));
          queryKeys.push(issueKeys.listPrefix(epicId));
          queryKeys.push(executionPlanKeys.detail(epicId));
        } else {
          queryKeys.push(issueKeys.all);
          queryKeys.push(executionPlanKeys.all());
        }
      } else if (entityType === "task") {
        // Invalidate all task lists
        queryKeys.push(taskKeys.lists());
        // Invalidate parent feature to update task count
        if (featureId) {
          queryKeys.push(featureKeys.detail(featureId));
        }
        if (epicId) {
          queryKeys.push(issueKeys.listPrefix(epicId));
          queryKeys.push(executionPlanKeys.detail(epicId));
        } else {
          queryKeys.push(issueKeys.all);
          queryKeys.push(executionPlanKeys.all());
        }
      }
      break;

    case "entity.updated":
      if (entityType === "epicRequest" && entityId) {
        queryKeys.push(epicRequestKeys.detail(entityId));
        queryKeys.push(epicRequestKeys.lists());
      } else if (entityType === "epic" && entityId) {
        // Invalidate specific epic and all lists
        queryKeys.push(epicKeys.detail(entityId));
        queryKeys.push(epicKeys.lists());
      } else if (entityType === "feature" && entityId) {
        // Invalidate specific feature and all lists
        queryKeys.push(featureKeys.detail(entityId));
        queryKeys.push(featureKeys.lists());
        // Also invalidate parent epic
        if (epicId) {
          queryKeys.push(epicKeys.detail(epicId));
          queryKeys.push(issueKeys.listPrefix(epicId));
          queryKeys.push(executionPlanKeys.detail(epicId));
        } else {
          queryKeys.push(issueKeys.all);
          queryKeys.push(executionPlanKeys.all());
        }
      } else if (entityType === "task" && entityId) {
        // Invalidate specific task and all lists
        queryKeys.push(taskKeys.detail(entityId));
        queryKeys.push(taskKeys.lists());
        // Invalidate parent feature
        if (featureId) {
          queryKeys.push(featureKeys.detail(featureId));
        }
        if (epicId) {
          queryKeys.push(issueKeys.listPrefix(epicId));
          queryKeys.push(executionPlanKeys.detail(epicId));
        } else {
          queryKeys.push(issueKeys.all);
          queryKeys.push(executionPlanKeys.all());
        }
      }
      break;

    case "entity.deleted":
      if (entityType === "epicRequest" && entityId) {
        queryKeys.push(epicRequestKeys.detail(entityId));
        queryKeys.push(epicRequestKeys.lists());
      } else if (entityType === "epic" && entityId) {
        // Invalidate specific epic and all lists
        queryKeys.push(epicKeys.detail(entityId));
        queryKeys.push(epicKeys.lists());
        // Also invalidate all features and tasks under this epic
        queryKeys.push(featureKeys.lists());
        queryKeys.push(taskKeys.lists());
      } else if (entityType === "feature" && entityId) {
        // Invalidate specific feature and all lists
        queryKeys.push(featureKeys.detail(entityId));
        queryKeys.push(featureKeys.lists());
        // Invalidate all tasks under this feature
        queryKeys.push(taskKeys.lists());
        // Invalidate parent epic
        if (epicId) {
          queryKeys.push(epicKeys.detail(epicId));
          queryKeys.push(issueKeys.listPrefix(epicId));
          queryKeys.push(executionPlanKeys.detail(epicId));
        } else {
          queryKeys.push(issueKeys.all);
          queryKeys.push(executionPlanKeys.all());
        }
      } else if (entityType === "task" && entityId) {
        // Invalidate specific task and all lists
        queryKeys.push(taskKeys.detail(entityId));
        queryKeys.push(taskKeys.lists());
        // Invalidate parent feature
        if (featureId) {
          queryKeys.push(featureKeys.detail(featureId));
        }
        if (epicId) {
          queryKeys.push(issueKeys.listPrefix(epicId));
          queryKeys.push(executionPlanKeys.detail(epicId));
        } else {
          queryKeys.push(issueKeys.all);
          queryKeys.push(executionPlanKeys.all());
        }
      }
      break;

    default:
      // For unknown event types, invalidate all lists as a safe fallback
      queryKeys.push(epicKeys.lists());
      queryKeys.push(featureKeys.lists());
      queryKeys.push(taskKeys.lists());
  }

  return queryKeys;
}

export interface UseLiveUpdatesOptions {
  url: string;
  epicId?: string;
  enabled?: boolean;
  /** Fallback poll cadence, ms. Defaults to LIST_FALLBACK_POLL_MS. */
  pollingInterval?: number;
}

/**
 * Hook that bridges SSE events to React Query cache invalidation
 * Falls back to polling when SSE connection is disconnected
 *
 * ⚠️ On Cloud Run the SSE path is the EXPENSIVE one, not the efficient one: a
 * held-open stream pins a billable instance and defeats --min-instances=0,
 * whereas this polling fallback costs a few short requests per tick. Read the
 * cost rationale in src/lib/polling.ts before "fixing" SSE into production.
 */
export function useLiveUpdates({
  url,
  epicId,
  enabled = true,
  pollingInterval = LIST_FALLBACK_POLL_MS,
}: UseLiveUpdatesOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const invalidationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingInvalidationsRef = useRef<Set<string>>(new Set());

  /**
   * Format event message for toast notification
   */
  const formatEventMessage = useCallback((event: SSEEvent): string => {
    const { type, data } = event;
    const eventData = data as EntityEventData;

    // Extract entity type from data
    const entityType = eventData.entityType || "item";
    const entity = entityType.charAt(0).toUpperCase() + entityType.slice(1);

    // Extract action from event type (e.g., "entity.updated" -> "updated")
    const action = type.split(".")[1] || "changed";

    return `${entity} ${action}`;
  }, []);

  /**
   * Debounced invalidation - batches multiple invalidations within 100ms window
   */
  const scheduleInvalidation = useCallback(
    (queryKeys: Array<readonly unknown[]>) => {
      // Add query keys to pending set (using JSON.stringify for deduplication)
      queryKeys.forEach((key) => {
        pendingInvalidationsRef.current.add(JSON.stringify(key));
      });

      // Clear existing timer if any
      if (invalidationTimerRef.current) {
        clearTimeout(invalidationTimerRef.current);
      }

      // Schedule batch invalidation after 100ms
      invalidationTimerRef.current = setTimeout(() => {
        const keysToInvalidate = Array.from(pendingInvalidationsRef.current).map(
          (key) => JSON.parse(key) as unknown[]
        );

        keysToInvalidate.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });

        // Clear pending set
        pendingInvalidationsRef.current.clear();
      }, 100);
    },
    [queryClient]
  );

  /**
   * Handle SSE events by invalidating relevant query keys
   * Wrapped in try-catch to prevent single event errors from breaking the connection
   */
  const handleEvent = useCallback(
    (event: SSEEvent) => {
      try {
        // Run progress snapshots (ENG-367) go straight into the useRunProgress
        // cache. Return early: getQueryKeysToInvalidate's default branch would
        // otherwise invalidate every list on each progress tick.
        if (event.type === RUN_PROGRESS_SSE_EVENT) {
          applyRunProgressEvent(queryClient, event.data);
          return;
        }

        // Show toast notification for entity change events
        // Suppress toasts for epicRequest events to avoid spam during planning progress
        if (event.type.startsWith("entity.")) {
          const eventData = event.data as EntityEventData;
          if (eventData.entityType !== "epicRequest") {
            const message = formatEventMessage(event);
            toast(message, 3000);
          }
        }

        const queryKeys = getQueryKeysToInvalidate(event, epicId);
        if (queryKeys.length > 0) {
          scheduleInvalidation(queryKeys);
        }
      } catch (error) {
        console.error("[LiveUpdates] Error processing event:", error, event);
        // Don't throw - let SSE connection continue despite single event errors
      }
    },
    [scheduleInvalidation, toast, formatEventMessage, epicId, queryClient]
  );

  /**
   * Polling fallback - invalidate all lists when disconnected
   */
  const startPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }

    pollingTimerRef.current = setInterval(() => {
      // When polling, invalidate all lists to ensure fresh data
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epicRequestKeys.lists() });
      // Also invalidate issue and execution plan queries used by Overview page
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
      queryClient.invalidateQueries({ queryKey: executionPlanKeys.all() });
    }, pollingInterval);
  }, [queryClient, pollingInterval]);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // Connect to SSE
  const { status } = useEventSource({
    url,
    ...(epicId && { epicId }),
    enabled,
    onEvent: handleEvent,
  });

  // Manage polling based on connection status
  useEffect(() => {
    if (status === "disconnected" || status === "polling") {
      // Start polling when disconnected or SSE has fallen back to polling
      console.log("[LiveUpdates] SSE unavailable, falling back to polling");
      startPolling();
    } else if (status === "connected") {
      // Stop polling when connected
      console.log("[LiveUpdates] SSE connected, stopping polling");
      stopPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
      if (invalidationTimerRef.current) {
        clearTimeout(invalidationTimerRef.current);
      }
    };
  }, [status, startPolling, stopPolling]);

  return { status };
}
